/**
 * The radio — Web Bluetooth against the vendor's GATT layout, and **the only file in the client
 * that knows a gate can be spoken to directly.**
 *
 * The protocol facts come from [`open-bt.md`](../../../docs/engineering/open-bt.md) and
 * `open-bt.py`, **not from decompiling the vendor app** (AGENTS.md §5). Plain BLE GATT, no
 * pairing, no encryption, no challenge–response: the session is a single write of
 * `PSW?<password>` followed by the same ASCII command the MQTT path carries, and the gate streams
 * `STA:` frames back on a notify characteristic.
 *
 * > `[ID:<serial>] → PSW?<password> → CMD:OpenN`
 *
 * **Stage 1 resident BLE is Android/Chrome only** (§7.12). Web Bluetooth does not exist in
 * WKWebView, so iOS residents open over cloud — *"not a degraded tier — they simply don't get the
 * BLE bonus"* — and {@link bluetoothSupported} is what lets a screen decide that **before**
 * offering an affordance rather than after a tap fails. iOS BLE is **P20**, which replaces this
 * leaf and nothing else.
 *
 * **Three things this module deliberately does not do:**
 *
 *  - **It never retries a password.** One `PSW?` per session, and an authentication failure
 *    returns `auth-failed` for the caller to answer with a re-fetch. D84/PEC-25 make guessing a
 *    prohibition rather than a preference: *"a client that cycled guesses would be
 *    indistinguishable from an attacker probing the controller."*
 *  - **It infers no presence.** Being in range is a heuristic for *transport selection* and never
 *    evidence of who is where (D86). Nothing here is stored, reported, or timestamped.
 *  - **It logs nothing.** The password is one `console.debug` away from Cache Storage via the
 *    devtools log, and D85 puts credential values out of every view, response, diff, and log.
 */

import { gateIsActing, gatePositionOfFrame, type GatePosition } from '@selogate/core'
import type { BleCredential } from './api'

/**
 * The vendor GATT defaults, from `open-bt.py`'s own constants.
 *
 * **These are protocol constants, not secrets**, and that distinction is what makes it correct to
 * compile them into the bundle: §13's *no gate BLE password in any client bundle* criterion is
 * about the credential, and a service UUID identifies a firmware family rather than a gate. A
 * controller that deviates carries per-gate overrides on its record, which arrive with the
 * credential and take precedence below.
 *
 * They are **not** broker strings, so CI check 1 is untouched: no hostname, no port, no `S-`/`C-`
 * topic prefix appears in `web/`, and a BLE gate is addressed by advertised name rather than by a
 * `BrokerCode` — which is also why a client-side adapter could never satisfy `TransportAdapter`
 * (`contracts.md` §3, and CI checks 1 and 6 behind it).
 */
export const VENDOR_SERVICE_UUID = '270cefc9-3fe9-56b9-2370-32f2b5277d50'

/**
 * **The services a live SEAV controller actually exposes — and the reason
 * {@link VENDOR_SERVICE_UUID} above is a *preference* rather than a requirement.**
 *
 * `270cefc9-…` came from one sniff, where it appears as *both* the service and the characteristic
 * UUID — which is what a copy-paste of a Web Bluetooth example looks like. Measured against a live
 * gate (`open-bt.md`, "The GATT layout, read back from hardware"), the peripheral advertises
 * `2102GBW`, connects cleanly, and exposes **`A002`** (the command and reply characteristics) and
 * **`A003`**. The sniffed UUID is not on the device at all.
 *
 * On Web Bluetooth that costs twice, and the second cost is the one with no symptom that names it:
 *
 *  - `getPrimaryService('270cefc9-…')` rejects `NotFoundError`, which {@link classify} reads as
 *    `unavailable` — about a second after the chooser closes, on a controller that is working
 *    perfectly.
 *  - **A service absent from `optionalServices` is invisible even to unfiltered discovery.** The
 *    fallback below cannot rescue a service the origin was never granted, so listing these is not
 *    an optimization — it is what makes discovery possible at all.
 *
 * The native leaf reached the same conclusion from the other side: `SeloGateBlePlugin.swift`
 * discovers unfiltered and falls back to every service, and `open-bt.py`'s `_pick_characteristics()`
 * has always done so. This is that rule, expressed in the one form Web Bluetooth accepts.
 */
export const MEASURED_SERVICE_UUIDS = [
  '0000a002-0000-1000-8000-00805f9b34fb',
  '0000a003-0000-1000-8000-00805f9b34fb',
] as const

/**
 * **The vendor command characteristic, measured on hardware rather than inherited from the sniff.**
 *
 * This was `0000c303-…`, which `open-bt.py`'s `DEFAULT_CHAR` still carries and whose comment sources
 * it to "the char at handle 0x0055". That layout is not this firmware's: a GATT dump of a live SEAV
 * controller puts `C303` at handle 50 and handle 55 at `C305`, the *notify* characteristic, and
 * neither of `open-bt.md`'s quoted handles (write 85, notify 82) appears at all.
 *
 * All three writable characteristics were tried against the gate, and the three outcomes are why
 * this was so hard to see from the client:
 *
 *  - **`C302`** (`write`) — **refused.** An ATT error on the write, surfacing as `disconnected`.
 *  - **`C303`** (`writeWithoutResponse`) — **accepted and ignored.** A write-without-response gets
 *    no receipt, so a wrong characteristic is *silent*: no error, no frames, no movement, and a
 *    `timeout` indistinguishable from an unpowered gate.
 *  - **`C304`** (`write`) — **opens the gate**: `PSW?…` → `PSW:Inst`, `CMD:OpenN` → `CMD:Ok`.
 *
 * The middle one is the trap worth remembering: **the wrong characteristic can fail without
 * producing a single symptom that names it.**
 */
export const VENDOR_WRITE_CHAR_UUID = '0000c304-0000-1000-8000-00805f9b34fb'

/** 🔸 The app uses ~100 ms between handshake writes; `open-bt.py` settled on 150. */
const INTER_WRITE_DELAY_MS = 150

/** 🔸 How long to wait for the gate's `STA:` acknowledgement before calling it a timeout. */
const REPLY_WINDOW_MS = 4_000

/** 🔸 How long a connect may take. Past this the gate is out of range in every way that matters. */
const CONNECT_TIMEOUT_MS = 6_000

/**
 * **How long the controller needs after the CCCD write before it will accept the first command —
 * measured on a live SEAV gate, and the reason a correct password produced total silence.**
 *
 * Enabling notifications is an asynchronous CCCD write, and `startNotifications()` resolving is not
 * the end of it as far as the *firmware* is concerned. **A `PSW?` written too soon is never
 * processed**, so the session never authenticates — and since the controller answers nothing at all
 * until it has, the whole exchange is silent.
 *
 * Measured, on the gate `2102GBW`, with a password the same controller had just accepted:
 *
 * | Gap, subscribe → first write | Elapsed since `connect()` | Result |
 * |---|---|---|
 * | ~32 ms (what this leaf did) | ~840 ms | **silence** — no `PSW:Inst`, no `PSW:Err`, no movement |
 * | 250 ms | ~1092 ms | `PSW:Inst`, then a full burst closing on `STA:End` |
 * | **~50 ms** | **~3.5 s** | **`PSW:Inst`** — and this row is the problem |
 *
 * **That third row was measured later and it undermines the reading above.** A 50 ms gap succeeded,
 * on a run whose connect-and-discovery phase happened to be slow. So the gap from the CCCD write is
 * **not** what governs this, or not alone — elapsed time since `connect()` fits all three rows and
 * the CCCD gap fits only two. **This constant may therefore be working by accident**, buying its
 * effect through total elapsed time rather than through where it sits, and a phone with faster
 * service discovery could still fail while passing every test written for it. Tracked as G-P20-j;
 * it is deliberately left where it is rather than moved on a two-point hunch.
 *
 * **A wrong password is loud** (`PSW:Err` at ~156 ms, terminal), which is what makes silence
 * diagnostic rather than ambiguous: a refused password answers, so a `PSW?` that answers nothing was
 * never read. That is what separates this from the CCCD race the Swift leaf paid for, which loses
 * frames from a session that is otherwise working.
 *
 * **The connection is not harmed, and that correction matters.** This was first recorded as *"the
 * controller discards the session and never recovers on that connection"*, inferred from a `STA:*?`
 * sent three seconds after a lost `PSW?` also returning nothing. That inference was wrong: a later
 * measurement showed the controller **never answers an unauthenticated `STA:*?`** — at 26 ms or at
 * 1000 ms — so that silence reported the missing authentication, not a poisoned link. Measured
 * directly: two early unauthenticated writes, then `PSW?` on the *same* connection → `PSW:Inst` and
 * a full burst. **Only the early `PSW?` is lost; everything else survives.**
 *
 * That is why {@link BleAdapter} still carries a fixed wait rather than a readiness probe: **there
 * is nothing to probe with.** The controller replies to nothing before authentication, so the only
 * available readiness signal is `PSW:Inst`/`PSW:Err` itself — and using it as one means sending
 * `PSW?` more than once, which is D84's territory and not a leaf's decision to take.
 *
 * 🔸 250 ms is the value that was measured working, not a threshold — the real one is somewhere
 * above 32 ms, and nothing here has found it. It is deliberately **not** `INTER_WRITE_DELAY_MS`:
 * that constant is the vendor's *inter-write* pacing, 150 ms, and reusing it here would be a guess
 * wearing the authority of a measurement.
 */
const NOTIFY_SETTLE_MS = 250

/**
 * 🔸 The budget for the **remembered** device — deliberately far shorter than the one above.
 *
 * A chooser-free connect that fails still has to leave enough of Chrome's transient user
 * activation (~5 s) for {@link BluetoothLike.requestDevice} to be called on the same tap.
 * Overspending here would turn a stale grant into a permanently dead button, which is worse than
 * the chooser it was trying to save.
 */
const REMEMBERED_CONNECT_TIMEOUT_MS = 2_000

/**
 * The ASCII command grammar, verbatim — the same strings the MQTT path delivers.
 *
 * **Exported for P20's native leaf, which emits these and composes none of its own.** The grammar
 * is P4's and `open-bt.md`'s; a second copy in `nativeBluetooth.ts` — or in Swift or Java — is the
 * fork P20 §3 refuses, and it would make the next firmware revision two edits instead of one.
 */
export const COMMANDS = {
  open: 'CMD:OpenN',
  pedestrian: 'CMD:OpenP',
} as const

export type BleCommand = keyof typeof COMMANDS

/**
 * §7.8 step 4's verification command — **a status query, and deliberately not an open.**
 *
 * The technician is standing at the gate with the leaf in reach, and possibly with somebody else
 * standing there too. Proving the handshake works must not actuate anything; the *test open* is
 * its own step immediately after, and it is the one that moves a leaf. `STA:*?` exercises exactly
 * the same path — connect, `PSW?<password>`, write, read the burst — so a pass here means the
 * credential the installer just typed is the one the controller accepts.
 */
export const STATUS_QUERY = 'STA:*?'

/**
 * What one local exchange concluded.
 *
 * **`failureReason` is the closed vocabulary the receipt schema declares**, so a failed local
 * attempt can be queued as a receipt with no translation step in between — and so no free text
 * can reach an append-only audit row from here.
 */
export type BleFailureReason = 'auth-failed' | 'timeout' | 'disconnected' | 'unavailable'

/**
 * What an exchange concluded, **including the one outcome that is not a failure of the transport
 * at all** — R4/D216.
 *
 * `'refused'` is the controller answering `CMD:Err`: authentication succeeded, the gate heard the
 * command, and it declined to carry it out. That is neither an authentication fault nor a
 * transport one, and it is the most certain thing this leaf ever learns — so it must not be filed
 * under {@link BleFailureReason}, whose members are the **receipt schema's closed vocabulary** and
 * reach an append-only audit row.
 *
 * The split follows `localOpen.ts`'s `'no-credential'` precedent exactly: a condition that writes
 * no receipt does not have to join the wire vocabulary in order to be named honestly on screen.
 */
export type BleOutcomeReason = BleFailureReason | 'refused'

/**
 * The frame prefix a healthy exchange answers with, and the one that closes the burst.
 *
 * Exported for the same reason {@link COMMANDS} is: P20's native leaf collects the burst on the
 * other side of the Capacitor bridge and hands it back here to be read, so the marker crosses the
 * bridge as a parameter rather than being re-typed in Swift and Java.
 */
export const BURST_PREFIX = 'STA:'
export const BURST_END_MARKER = 'STA:End'

/**
 * **The command acknowledgement, and the half of the grammar `STA:` never described.**
 *
 * A status query (`STA:*?`) is answered with a `STA:` burst closed by `STA:End`. A **command** is
 * not: measured on a live SEAV controller, `PSW?…` is answered `PSW:Inst` and `CMD:OpenN` is
 * answered **`CMD:Ok`** or **`CMD:Err`**, and no `STA:` frame appears at all. `open-bt.py` shows
 * the same two lines and never needed to name them, because a human reads its output.
 *
 * That absence had two consequences, and they compounded. `BURST_END_MARKER` never arrived, so
 * every command burned the whole reply window and then reported a transport `timeout`; and if it
 * had returned, {@link readBurst} would have called a burst with no `STA:` frame `auth-failed` —
 * **on a gate that had just opened, after an authentication the controller explicitly accepted.**
 * A successful open reported as an authentication failure is the exact class of false statement
 * D134 and D135 exist to forbid.
 */
export const COMMAND_ACK_PREFIX = 'CMD:'
export const COMMAND_ACK_OK = 'CMD:Ok'

/**
 * **The password refusal — terminal, and the reason a burst needs more than one terminator.**
 *
 * `PSW?<password>` is answered `PSW:Inst` on acceptance and **`PSW:Err`** on refusal, and a refused
 * session produces nothing further: no `STA:` frames and no `CMD:` acknowledgement. Measured on a
 * live gate at roughly two attempts in five, with the *same* four characters that succeed seconds
 * either side of it.
 *
 * **Two later measurements narrow what this frame proves, without changing what this leaf does.**
 * A *second* `PSW?` on an already-authenticated session also answers `PSW:Err`, and the session
 * **survives** it — a `STA:*?` afterwards returned a full burst. So `PSW:Err` means *this `PSW?`
 * was not accepted*, carries no claim about the credential, and is not reliably terminal. Nothing
 * here changes, because this leaf sends exactly one `PSW?` per session (D84) and in that shape the
 * frame can only mean a refusal. **It is the reason a retry is not the answer to a lost reply:** a
 * `PSW?` re-sent after a *successful* first attempt earns `PSW:Err`, which becomes `auth-failed`,
 * which sends PEC-25 to re-fetch a credential that was never wrong.
 *
 * Without this in the terminal set a refused session has nothing to close on, so the leaf sits out
 * the whole reply window and returns a **transport** `timeout` — which outranks the frames, so
 * {@link readBurst} is never consulted and the refusal never becomes `auth-failed`. That is not
 * cosmetic: **PEC-25's credential re-fetch is reached through `auth-failed` and nothing else**, so
 * a refusal read as a timeout means the client never re-fetches, and the resident waits ~6 s to be
 * told the wrong thing. D84 still forbids retrying the *password*; re-fetching the credential is
 * the permitted response, and this is what reaches it.
 */
export const PASSWORD_REFUSED = 'PSW:Err'

/**
 * Whether a frame ends the burst. **Three terminators, because the controller has three ways to
 * stop talking:** a status burst closes with `STA:End`, a command with `CMD:Ok`/`CMD:Err`, and a
 * refused password with `PSW:Err`, after which nothing further arrives at all.
 */
export function burstEnded(frame: string): boolean {
  return (
    frame.startsWith(BURST_END_MARKER) ||
    frame.startsWith(COMMAND_ACK_PREFIX) ||
    frame.startsWith(PASSWORD_REFUSED)
  )
}

/**
 * What a collected burst means — **the one place a set of frames becomes a verdict.**
 *
 * The vendor answers a bad password by ignoring the command, so an authentication failure is
 * visible only as a burst that never mentions the gate's state. 🔸 The frames the sniff recorded on
 * a healthy exchange all begin `STA:`; a session that produced frames but no `STA:` is read as
 * `auth-failed`, which is what makes PEC-25's re-fetch reachable rather than leaving every bad
 * password looking like a timeout.
 *
 * **An empty burst is `timeout`, never a success.** The write may well have opened the gate — ATT
 * write-without-response gives no receipt — so this is PEC-8's *"couldn't confirm"* rather than a
 * failure, and the caller must not present it as a gate that did not move. It is also, deliberately,
 * **not** queued as a successful receipt: a receipt asserts a physical opening, and nothing
 * witnessed one.
 *
 * Shared by both radios so that a native exchange and a Web Bluetooth one cannot disagree about
 * what the same frames meant.
 */
export function readBurst(frames: readonly string[]): 'ok' | BleOutcomeReason {
  if (frames.length === 0) return 'timeout'

  /*
   * **A command acknowledgement outranks the `STA:` test, because a command burst has no `STA:`
   * frame in it.** Checking the prefix first would read every successful open as `auth-failed`.
   */
  if (frames.some((frame) => frame.startsWith(COMMAND_ACK_PREFIX))) {
    if (frames.some((frame) => frame.startsWith(COMMAND_ACK_OK))) return 'ok'
    /*
     * `CMD:Err` is the controller **refusing** the command — authentication succeeded (`PSW:Inst`
     * precedes it) and the gate answered, so this is neither `auth-failed` nor a transport fault.
     *
     * **It used to return `timeout`, and that was the product decision this branch was waiting
     * for** (R4/D216). Observed in the field as the ordinary answer to an open requested while the
     * gate is already opening, which a resident double-tapping hits routinely — and `timeout`'s
     * copy is *"the command was sent but the gate did not confirm"*, byte-identical in Hebrew to
     * the cloud's own unconfirmed sentence. So the single most **certain** outcome the radio ever
     * reports was rendered as the least certain one, on a gate that was visibly moving.
     *
     * {@link BleOutcomeReason} carries it without widening the receipt vocabulary, on the
     * `'no-credential'` precedent. `readBurst` states what the frames meant; whether that queues a
     * receipt is `localOpen.ts`'s call, and it does not.
     */
    return 'refused'
  }

  if (!frames.some((frame) => frame.startsWith(BURST_PREFIX))) return 'auth-failed'
  return 'ok'
}

/**
 * **R4/D225 — what a set of frames means when a command exchange carries `STA:` frames it did not
 * ask for.**
 *
 * D225 appended {@link STATUS_QUERY} to a command on both radios; **D232 took that write off BLE**
 * (see {@link planExchange}), and this reader is unchanged by that, because the frames it defends
 * against were never only the solicited ones: the controller announces state changes
 * spontaneously, so a command exchange can come back holding `STA:` frames the command itself
 * never produced. Reading those with {@link readBurst} would answer `'ok'` for **any reachable
 * gate** — the frames carry `STA:` and no `CMD:`, which is precisely its rule for a healthy
 * *status* exchange — so every silent open would report success. That is why this is a second
 * reader rather than a widened one.
 *
 * The order is the one `packages/transport/src/command.ts` keeps on the cloud path, for the same
 * reason: **the command's own answer outranks the position.**
 *
 *  1. Everything {@link readBurst} settles without looking at a position — an empty burst, a refused
 *     password, `CMD:Err` (D216's `'refused'`, which D223 routes to Success), and `CMD:Ok`.
 *  2. Only where the command answered nothing at all, the gate's own reported position: `opening` /
 *     `open` / `closing` / `stopped-while-*` is `'ok'`, and `closed` — or no position — is
 *     `'timeout'`, the same silence-shaped Failed the resident already sees.
 *
 * 🔸 **`'timeout'` is reused rather than split.** A gate that answered `closed` is a different fact
 * from a gate that answered nothing, and the cloud path keeps that distinction in its audit trail
 * (`not-acting` vs `timeout`). Here there is nowhere to keep it: a local refusal writes **no
 * receipt** (D216(c)), §10 forbids a new resident-facing sentence, and `BleFailureReason` is the
 * receipt schema's closed vocabulary — so a new member would be a wire change for a case that
 * reaches no wire. **Vetoable**, and the thing to change if the pilot ever wants BLE refusals
 * counted.
 */
export function readCommandBurst(frames: readonly string[]): 'ok' | BleOutcomeReason {
  const verdict = readBurst(frames)
  if (verdict !== 'ok') return verdict
  // `readBurst` says `'ok'` for two different reasons; only one of them is the command answering.
  if (frames.some((frame) => frame.startsWith(COMMAND_ACK_OK))) return 'ok'

  /*
   * The last `Centr=` wins: frames arrive in order, and a command exchange can carry more than one
   * — the appended query's reply and, later, the controller's own state-change announcement.
   */
  let position: GatePosition | undefined
  for (const frame of frames) position = gatePositionOfFrame(frame) ?? position
  return gateIsActing(position) ? 'ok' : 'timeout'
}

/**
 * **What one exchange writes, what closes it, and how its frames are read — decided once, for all
 * three BLE leaves** (R4/D225, amended by D232).
 *
 * The three answers move together and are wrong apart, and all three leaves take them from here,
 * which is what makes *a defect fixed in one leaf is fixed in one* (AGENTS.md §4.9) true of this
 * change rather than a hope about it.
 *
 * **D232 removed the appended {@link STATUS_QUERY} write from a BLE command, and the cloud path
 * keeps its own.** D225 shipped one shape for both radios on a symmetry argument; the measurement
 * that followed took the argument away. On BLE a controller answers a command with a real protocol
 * acknowledgement — `CMD:Ok` at ~240 ms even to a tap on a gate **already travelling** — so the
 * exchange has always closed on the command's own terminator and the appended reply was discarded
 * by the precedence below every time. The defect D225 exists to fix is the **cloud** path's, where
 * a frame is emitted on physical state change and a second tap therefore announces nothing.
 *
 * **The one silence that looks like a counter-example is not one.** R4 **F7** reproduced a live
 * second tap over BLE returning *zero* frames — but zero frames is zero frames: a session that
 * cannot answer the command cannot answer a query written down the same session either, so the
 * second write rescues nothing there. It could only ever have helped where the command is silent
 * **and** the session works, and on this radio that combination has never been observed. What
 * produced F7's silence is the app's own connection lifecycle, which is a third mechanism and is
 * still owed evidence.
 *
 * **{@link readCommandBurst} stays, and that is deliberate rather than leftover.** The controller
 * emits spontaneous state-change frames, so a command exchange can still carry a `STA:` frame this
 * leaf never asked for — which is precisely what {@link readBurst} alone would misread as success.
 * The position branch is now reached by a spontaneous announcement rather than by a solicited
 * reply, and it means the same thing: the gate says it is acting.
 *
 * **`STA:End` is deliberately NOT a terminator for a command, and that is the part to read twice.**
 * R4 §4.1 anticipated the opposite — *"a combined burst must also accept `STA:End`"* — and the
 * measurement behind D225 is what reverses it: a query answers in ~387 ms while a **command's** own
 * frame is emitted on **motor start**, ~1,532 ms in. So a status reply that closed the exchange
 * would end it a second before the gate had anything to say, and hand the reader a position taken
 * **before the motor started** — `closed` on a gate that was about to open, which is Failed on a
 * working open and the exact inversion R4 exists to remove. D225's own non-negotiable constraint
 * says it plainly: *the status read may only decide a verdict the command left undecided*. The cost
 * is that a second tap on a moving gate waits out the reply window before being told Success — the
 * right answer, not a faster one.
 */
export interface BleExchangePlan {
  /** Written in order, after `PSW?`, spaced by the leaf's inter-write delay. */
  readonly writes: readonly string[]
  /** Prefixes that close the exchange. Compared as prefixes, on both sides of the bridge. */
  readonly endMarkers: readonly string[]
  /** Whether a frame closes the exchange — {@link endMarkers}, for the leaf that collects in JS. */
  readonly ended: (frame: string) => boolean
  /** What the collected frames concluded. */
  readonly read: (frames: readonly string[]) => 'ok' | BleOutcomeReason
}

export function planExchange(command: string): BleExchangePlan {
  const statusOnly = command === STATUS_QUERY
  const endMarkers = statusOnly
    ? [BURST_END_MARKER, PASSWORD_REFUSED]
    : [COMMAND_ACK_PREFIX, PASSWORD_REFUSED]
  return {
    // **One write, always (D232).** §7.8 step 4's verification asks one question and must keep
    // asking exactly one — it is the installer proving a credential — and since D232 a command
    // asks exactly one too, this radio having a real acknowledgement to close on.
    writes: [command],
    endMarkers,
    ended: (frame) => endMarkers.some((marker) => frame.startsWith(marker)),
    read: statusOnly ? readBurst : readCommandBurst,
  }
}

export type BleOpenResult =
  | { readonly ok: true; readonly latencyMs: number }
  | { readonly ok: false; readonly reason: BleOutcomeReason; readonly latencyMs: number }

/**
 * The seam. A screen depends on this interface; `webBluetoothAdapter` is the body, and tests
 * supply their own — the same shape `device-capabilities/*` uses, for the same reason: a browser
 * capability behind an interface is testable, and a `navigator.bluetooth` reached for directly is
 * not.
 */
export interface BleAdapter {
  /** Whether this browser exposes Web Bluetooth at all. Synchronous, prompts nothing. */
  supported(): boolean
  /**
   * Whether this gate is **in range now**, cheaply enough to ask before every open.
   *
   * Returns `undefined` when the platform cannot answer without prompting — which on Web
   * Bluetooth is the ordinary case, see {@link webBluetoothAdapter}. **`undefined` is not
   * `false`**: it means *unknown*, and the caller must not render it as "not in range".
   */
  inRange(credential: BleCredential): Promise<boolean | undefined>
  open(credential: BleCredential, command: BleCommand): Promise<BleOpenResult>
  /**
   * §7.8 step 4's live check: the same handshake, ending in `STA:*?` rather than an open.
   *
   * A separate method rather than a third `BleCommand`, because the two differ in the only way
   * that matters at a gate — one actuates and one does not — and a caller choosing between them
   * from a string is a caller one typo away from opening a barrier during a verification.
   */
  verify(credential: BleCredential): Promise<BleOpenResult>
}

/*
 * ---------------------------------------------------------------------------------------
 * The Web Bluetooth types, declared narrowly rather than pulled in wholesale.
 *
 * `@types/web-bluetooth` would be a dependency added for four method signatures, and this
 * repository's install is run by hand (AGENTS.md §5). What is declared here is exactly what is
 * called, which also documents the surface this module depends on.
 * ---------------------------------------------------------------------------------------
 */

interface GattCharacteristic {
  readonly uuid?: string
  writeValue(value: BufferSource): Promise<void>
  startNotifications(): Promise<GattCharacteristic>
  addEventListener(type: 'characteristicvaluechanged', listener: (event: Event) => void): void
  removeEventListener(type: 'characteristicvaluechanged', listener: (event: Event) => void): void
  readonly value?: DataView
  readonly properties?: {
    readonly notify?: boolean
    readonly indicate?: boolean
    readonly write?: boolean
    readonly writeWithoutResponse?: boolean
  }
}

interface GattService {
  readonly uuid?: string
  getCharacteristic(uuid: string): Promise<GattCharacteristic>
  getCharacteristics(): Promise<GattCharacteristic[]>
}

interface GattServer {
  connect(): Promise<GattServer>
  disconnect(): void
  getPrimaryService(uuid: string): Promise<GattService>
  getPrimaryServices?(): Promise<GattService[]>
  readonly connected?: boolean
}

interface BluetoothDeviceLike {
  readonly name?: string
  readonly gatt?: GattServer
  watchAdvertisements?(options?: { signal?: AbortSignal }): Promise<void>
  addEventListener(type: 'advertisementreceived', listener: () => void): void
  removeEventListener(type: 'advertisementreceived', listener: () => void): void
}

interface BluetoothLike {
  requestDevice(options: {
    filters: { name?: string }[]
    optionalServices?: string[]
  }): Promise<BluetoothDeviceLike>
  /**
   * Devices this origin has **already** been granted — Chrome's persistent-permissions backend,
   * and the only chooser-free path the web platform has. Optional because it is not in every
   * build; see {@link rememberedDevice}.
   */
  getDevices?(): Promise<BluetoothDeviceLike[]>
  getAvailability?(): Promise<boolean>
}

const bluetooth = (): BluetoothLike | undefined =>
  (globalThis.navigator as { bluetooth?: BluetoothLike } | undefined)?.bluetooth

const encode = (text: string): Uint8Array => new TextEncoder().encode(text)

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Whether `requestDevice()` would still be allowed to prompt — **`undefined` where the platform
 * cannot say.** D189(c).
 *
 * **Not a heuristic dressed as a fact, and this is the one place the distinction is expensive.**
 * Chrome gates the chooser on *transient user activation*, a window of roughly five seconds that
 * `requestDevice` also **consumes**. `REQUEST_TIMEOUT_MS` is twenty. So an automatic BLE fallback
 * fired after a cloud request ran its full budget reaches the picker with no activation left, gets
 * `NotAllowedError`, and {@link classify} reads that as `'unavailable'` — which renders as *"could
 * not reach the gate over Bluetooth"*, **about a radio that was never switched on**. That is the
 * same false claim D134/D135 exist to forbid on the other side of the exchange, arriving from a
 * platform rule rather than from an optimistic reading of a response.
 *
 * `navigator.userActivation` ships in exactly the Chromium builds that have Web Bluetooth, so on
 * the surface §7.12 gives Stage-1 BLE to, this is an answer rather than a guess. Elsewhere it is
 * absent and the caller falls back to an elapsed-time budget — which is why this returns
 * `undefined` rather than `false`: *unknown* and *expired* lead to different decisions, and
 * collapsing them would refuse the fallback on every platform that simply does not implement the
 * API. The container needs none of this: `usesNativeBle()` has no picker to be allowed.
 */
export function userActivationLive(): boolean | undefined {
  const nav = globalThis.navigator as { userActivation?: { isActive?: boolean } } | undefined
  const active = nav?.userActivation?.isActive
  return typeof active === 'boolean' ? active : undefined
}

/**
 * The real adapter.
 *
 * **`requestDevice` requires a user gesture and shows a chooser**, which is the platform
 * constraint that shapes everything above it: there is no silent scan on the web, so
 * {@link BleAdapter.inRange} cannot answer without prompting and returns `undefined` rather than
 * guessing. That is why §6's auto-pick is **cloud-biased by default** and upgrades only on
 * evidence — the web platform simply does not offer the evidence unprompted, and inventing it
 * would be exactly the presence inference D86 forbids.
 */
export const webBluetoothAdapter: BleAdapter = {
  supported: (): boolean => bluetooth()?.requestDevice !== undefined,

  async inRange(): Promise<boolean | undefined> {
    /*
     * **Unknown, not false, and this is the honest answer rather than a stub.** Chrome's
     * `watchAdvertisements()` is behind a flag and still requires a device the user has already
     * chosen; `getAvailability()` reports whether the *adapter* exists, not whether this gate is
     * near. Reporting "not in range" from either would put a false negative in front of a
     * resident standing at their own gate.
     */
    const availability = await bluetooth()?.getAvailability?.()
    return availability === false ? false : undefined
  },

  open(credential: BleCredential, command: BleCommand): Promise<BleOpenResult> {
    return exchange(credential, COMMANDS[command])
  },

  verify(credential: BleCredential): Promise<BleOpenResult> {
    return exchange(credential, STATUS_QUERY)
  },
}

/**
 * One BLE session: connect, authenticate, write **one** command, read the burst, disconnect.
 *
 * Both `open` and `verify` go through here, so the handshake exists once — which is what makes
 * "the verification exercises the same path as the open" a fact rather than an intention.
 */
async function exchange(credential: BleCredential, command: string): Promise<BleOpenResult> {
  {
    const startedAt = Date.now()
    const elapsed = (): number => Date.now() - startedAt
    // D225 — the writes, the terminators and the reader come from one place, for both radios.
    const plan = planExchange(command)

    const api = bluetooth()
    if (api === undefined) return { ok: false, reason: 'unavailable', latencyMs: elapsed() }

    const preferredService = credential.bleServiceUuid ?? VENDOR_SERVICE_UUID
    /*
     * **Every service this origin may look at, and the list is not an optimization.** Web
     * Bluetooth grants access per service UUID: one absent from `optionalServices` cannot be read
     * even by unfiltered discovery, so the fallback in {@link discoverServices} would find nothing
     * to fall back *to*. The record's override comes first, then the sniffed UUID, then the two
     * services measured on hardware.
     */
    const allowedServices = [...new Set([preferredService, VENDOR_SERVICE_UUID, ...MEASURED_SERVICE_UUIDS])]
    let server: GattServer | undefined

    try {
      /*
       * **The chooser is skipped when this origin already holds a grant for the gate**, and
       * `getDevices()` is the only way the web platform offers to do that — see
       * {@link rememberedDevice}. Where it is absent, which is the ordinary case, this is
       * `undefined` and the next line is exactly what P16 shipped.
       */
      let services: readonly GattService[] = []
      const remembered = await rememberedDevice(api, credential.bleName)
      if (remembered?.gatt !== undefined) {
        server = await withTimeout(
          remembered.gatt.connect().catch(() => undefined),
          REMEMBERED_CONNECT_TIMEOUT_MS,
        )
        if (server !== undefined) {
          services = await discoverServices(server, preferredService)
          /*
           * **A grant carries the `optionalServices` list it was made with, and a narrower one
           * cannot be widened without a fresh grant.** A device granted by a build that listed only
           * the sniffed UUID would hand back no readable service here — forever, and with no
           * chooser to repair it, since this path is what skipped the chooser. So an empty
           * discovery gives the picker its turn rather than reporting the gate unreachable.
           */
          if (services.length === 0) {
            disconnectQuietly(server)
            server = undefined
          }
        }
        if (server === undefined) disconnectQuietly(remembered.gatt)
      }

      if (server === undefined) {
        // Filtered by advertised name: on macOS/Chrome, CoreBluetooth exposes system UUIDs rather
        // than MAC addresses, so the name is the only stable handle (AGENTS.md §5).
        const device = await api.requestDevice({
          filters: [{ name: credential.bleName }],
          optionalServices: allowedServices,
        })
        if (device.gatt === undefined) {
          return { ok: false, reason: 'unavailable', latencyMs: elapsed() }
        }

        server = await withTimeout(device.gatt.connect(), CONNECT_TIMEOUT_MS)
        /*
         * **A connect that never completed is `unavailable`, not `timeout` — the chooser having
         * seen the device does not mean this phone spoke to it.** The distinction the two words
         * carry is the one `statusPhaseOf` keeps on the cloud side and `'no-credential'` keeps
         * two returns above: `timeout` asserts *we addressed the gate and it went quiet*, which is
         * why `localOpen.ts` lets it queue a receipt and lets the screen tell the resident to go
         * check the gate. Past this line nothing has been written — no `PSW?`, no command — so
         * both of those are claims about an exchange that did not happen. The receipt is the half
         * that keeps: a sentence is gone on the next tap, and a Bluetooth failure row against a
         * gate that was never addressed is what a manager reads afterwards.
         *
         * `replies === undefined` below stays `timeout` for the same rule read the other way: the
         * writes have gone out by then, and a write-without-response yields no receipt, so the
         * gate may well have moved.
         */
        if (server === undefined) return { ok: false, reason: 'unavailable', latencyMs: elapsed() }
        services = await discoverServices(server, preferredService)
      }

      /*
       * **The characteristic gets the same vendor default the service already had.** This line
       * read `credential.bleCharUuid` alone, so a gate with no override — which is every gate, since
       * the install flow writes only a name and a password — fell through to "the first
       * characteristic declaring `write`". On this firmware that is `C302`, which **refuses every
       * write**. `nativeBluetooth.ts` was corrected on a live gate and this leaf was not; the two
       * now default identically.
       */
      const { write, notify } = await pickCharacteristics(
        services,
        credential.bleCharUuid ?? VENDOR_WRITE_CHAR_UUID,
      )
      if (write === undefined) return { ok: false, reason: 'unavailable', latencyMs: elapsed() }

      /*
       * **Awaited before the first write, and that `await` is a defect the native leaves paid for
       * twice.** `startNotifications()` is not a local flag: it is an asynchronous CCCD write, and
       * the peripheral sends nothing until it has been acknowledged. A session that starts writing
       * first loses the whole burst — the gate opens and the app reports that it never answered.
       * Chrome happens to serialize GATT operations per device, so the ordering held by accident;
       * `SeloGateBlePlugin.swift` shipped on the same accident and lost it on hardware.
       */
      const replies = notify === undefined ? undefined : await listen(notify)

      // The session order the app uses. `ID:<serial>` first when the record carries one.
      if (credential.bleSerial !== undefined) {
        await write.writeValue(encode(`ID:${credential.bleSerial}`))
        await wait(INTER_WRITE_DELAY_MS)
      }
      await write.writeValue(encode(`PSW?${credential.blePassword}`))
      /*
       * **One `PSW?` (D84), then whatever this exchange asks** — one write since D232, for a
       * verification and for an open alike. The loop stays a loop rather than collapsing to a
       * single call: {@link BleExchangePlan} owns how many writes an exchange makes, and every one
       * of them keeps the vendor's pacing, because the handshake is stateful and a write that
       * arrived on the heels of the last is the shape that works a few times and then refuses
       * permanently (AGENTS.md §4.9).
       */
      for (const payload of plan.writes) {
        await wait(INTER_WRITE_DELAY_MS)
        await write.writeValue(encode(payload))
      }

      /*
       * **No notify characteristic means no acknowledgement, and that is a `timeout`.**
       * The write may well have opened the gate — ATT write-without-response gives no receipt —
       * so this is PEC-8's *"couldn't confirm"* rather than a failure, and the caller must not
       * present it as a gate that did not move. It is also, deliberately, **not** queued as a
       * successful receipt: a receipt asserts a physical opening, and nothing witnessed one.
       */
      if (replies === undefined) return { ok: false, reason: 'timeout', latencyMs: elapsed() }

      const burst = await replies.collect(REPLY_WINDOW_MS, plan.ended)
      const verdict = plan.read(burst)
      if (verdict !== 'ok') return { ok: false, reason: verdict, latencyMs: elapsed() }
      return { ok: true, latencyMs: elapsed() }
    } catch (error) {
      // **The error is classified and never logged.** On this path an error object is one field
      // away from the password that produced it — the same rule `ble-access.ts` keeps on the
      // server, where it is the one place a message is deliberately not recorded.
      return { ok: false, reason: classify(error), latencyMs: elapsed() }
    } finally {
      if (server !== undefined) disconnectQuietly(server)
    }
  }
}

/** A disconnect that throws has nothing left to tell us; the exchange is already over. */
function disconnectQuietly(target: { disconnect(): void }): void {
  try {
    target.disconnect()
  } catch {
    // Already gone, which is the state we were asking for.
  }
}

/**
 * A device this origin has **already** been granted, matched by advertised name.
 *
 * **This is the whole of the chooser bypass the web platform has, and on stock Chrome it does
 * nothing.** `requestDevice()`'s picker *is* the permission grant: it requires a user gesture, it
 * cannot be suppressed, and there is no API that scans on a page's behalf — which is deliberate,
 * since a silent scan is a fingerprinting surface and a presence signal at once. `getDevices()`
 * returns grants already given, so where Chrome exposes it the resident meets the chooser **once
 * per gate** instead of once per open. It ships behind the persistent-permissions backend
 * (`chrome://flags/#enable-web-bluetooth-new-permissions-backend`), so the member is optional and
 * the ordinary answer here is `undefined`.
 *
 * **The container is the real answer**, and it is already built: `SeloGateBlePlugin.java` scans by
 * name with no picker at all, behind one `BLUETOOTH_SCAN`/`BLUETOOTH_CONNECT` grant.
 *
 * Nothing here is stored, timestamped, or reported: a grant is a permission record, and reading one
 * says nothing about who is where (D86).
 */
async function rememberedDevice(api: BluetoothLike, name: string): Promise<BluetoothDeviceLike | undefined> {
  if (api.getDevices === undefined) return undefined
  try {
    const granted = await api.getDevices()
    return granted.find((device) => device.name === name)
  } catch {
    // A backend that refuses to enumerate grants has told us nothing except to use the chooser.
    return undefined
  }
}

/**
 * The services to look for characteristics in — **the preferred one when it is there, and every
 * one on the device when it is not.**
 *
 * That fallback is `open-bt.py`'s rule (`_pick_characteristics()`) and the Swift leaf's, and the
 * reason both have it is in {@link MEASURED_SERVICE_UUIDS}: the sniffed vendor service is not on
 * the shipping firmware, so a client that discovers only the named service finds nothing and
 * reports a healthy gate unreachable in about a second.
 */
async function discoverServices(server: GattServer, preferredUuid: string): Promise<readonly GattService[]> {
  try {
    const all = (await server.getPrimaryServices?.()) ?? []
    const preferred = all.find((service) => sameUuid(service.uuid, preferredUuid))
    if (preferred !== undefined) return [preferred]
    if (all.length > 0) return all
  } catch {
    // Fall through: a browser without unfiltered discovery still answers a named service.
  }

  try {
    return [await server.getPrimaryService(preferredUuid)]
  } catch {
    // **Not an error yet.** The caller reads an empty list as `unavailable`, which is what it is:
    // the radio connected and found nothing it was allowed to speak to.
    return []
  }
}

/** UUID comparison. Chrome normalizes to lower case; a record's override may not be. */
function sameUuid(a: string | undefined, b: string | undefined): boolean {
  return a !== undefined && b !== undefined && a.toLowerCase() === b.toLowerCase()
}

/**
 * The write and notify characteristics, preferring the record's override.
 *
 * **Across every discovered service, and picked by declared property** — `open-bt.md` maps write
 * handle 85 and notify handle 82, neither of which exists on the shipping firmware, and ATT `0x0D`
 * *"usually means the wrong write characteristic"*. Property beats position, and an explicit UUID
 * beats both: the property fallback lands on `C302` here, which refuses every write.
 *
 * **The override applies to the write characteristic only.** It used to be returned as *both*, and
 * on this firmware that is wrong in the silent direction: `C304` does not notify, so
 * `startNotifications()` rejects, no frame is ever collected, and the exchange reports a `timeout`
 * on a gate that answered.
 */
async function pickCharacteristics(
  services: readonly GattService[],
  overrideUuid: string | undefined,
): Promise<{ write?: GattCharacteristic; notify?: GattCharacteristic }> {
  const all: GattCharacteristic[] = []
  for (const service of services) {
    try {
      all.push(...(await service.getCharacteristics()))
    } catch {
      // One service that refuses enumeration is not the end of the session; only the last is.
    }
  }

  if (all.length === 0) {
    // A firmware that refuses enumeration still answers the documented UUID.
    for (const service of services) {
      try {
        const characteristic = await service.getCharacteristic(overrideUuid ?? VENDOR_WRITE_CHAR_UUID)
        return { write: characteristic, notify: characteristic }
      } catch {
        // Try the next service before giving up.
      }
    }
    return {}
  }

  const write =
    (overrideUuid === undefined ? undefined : all.find((c) => sameUuid(c.uuid, overrideUuid))) ??
    all.find((c) => c.properties?.write === true) ??
    all.find((c) => c.properties?.writeWithoutResponse === true)
  const notify =
    all.find((c) => c.properties?.notify === true) ?? all.find((c) => c.properties?.indicate === true)

  return { ...(write === undefined ? {} : { write }), ...(notify === undefined ? {} : { notify }) }
}

/**
 * Collects reply frames for a bounded window, then stops listening.
 *
 * **Async, because the CCCD write it starts has to land before the first command goes out.** See
 * the call site: the peripheral sends nothing until it has acknowledged this, and a burst that
 * arrives before anyone is listening is a gate that opened and an app that says it did not.
 * A rejection is *not* fatal — a controller that refuses notifications still accepts commands, so
 * the writes go out either way and the empty burst becomes PEC-8's "couldn't confirm".
 */
async function listen(
  characteristic: GattCharacteristic,
): Promise<{ collect(ms: number, ended?: (frame: string) => boolean): Promise<string[]> }> {
  const frames: string[] = []
  const decoder = new TextDecoder()
  const onValue = (): void => {
    const value = characteristic.value
    if (value !== undefined) frames.push(decoder.decode(value).trim())
  }

  try {
    await characteristic.startNotifications()
    characteristic.addEventListener('characteristicvaluechanged', onValue)
    /*
     * **The settle belongs here rather than at the call site**, so that no caller can subscribe and
     * write without it. See {@link NOTIFY_SETTLE_MS}: without this the controller discards the
     * whole session and a correct password is indistinguishable from a dead radio.
     */
    await wait(NOTIFY_SETTLE_MS)
  } catch {
    // No notifications. The writes still go out; the collector below returns an empty burst.
  }

  return {
    /**
     * **Which frames close this exchange is the caller's business** (`planExchange`), and it stopped
     * being a constant in D225: a status query ends on `STA:End`, a command on `CMD:…`, and either
     * on `PSW:Err`. Passing {@link burstEnded} — the union of all three — would let the appended
     * query's own reply end a command's exchange, which is the one thing D225 may not do.
     */
    async collect(ms: number, ended: (frame: string) => boolean = burstEnded): Promise<string[]> {
      const deadline = Date.now() + ms
      while (Date.now() < deadline && !frames.some((frame) => ended(frame))) {
        await wait(50)
      }
      characteristic.removeEventListener('characteristicvaluechanged', onValue)
      return frames
    },
  }
}

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), ms)
  })
  try {
    return await Promise.race([promise, timeout])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

/**
 * A thrown error's name, mapped onto the receipt vocabulary.
 *
 * `NotFoundError` covers both "the user dismissed the chooser" and "no device matched", and Web
 * Bluetooth does not distinguish them. Both are `unavailable` — **not** `timeout`, because a
 * dismissed chooser sent nothing and the gate cannot have moved, and calling it a timeout would
 * put PEC-8's "couldn't confirm" in front of a resident who simply cancelled.
 */
function classify(error: unknown): BleFailureReason {
  const name =
    typeof error === 'object' && error !== null && 'name' in error && typeof error.name === 'string'
      ? error.name
      : undefined
  if (name === 'NetworkError') return 'disconnected'
  if (name === 'SecurityError' || name === 'NotAllowedError') return 'unavailable'
  if (name === 'NotSupportedError' || name === 'NotFoundError') return 'unavailable'
  return 'disconnected'
}

/** Whether this browser exposes Web Bluetooth. Ask before offering the affordance, not after. */
export function bluetoothSupported(adapter: BleAdapter = webBluetoothAdapter): boolean {
  return adapter.supported()
}

let installed: BleAdapter = webBluetoothAdapter

/**
 * Swap the adapter — **the composition seam, and the only one.**
 *
 * It exists for the same two reasons `configureTransportAdapter` does on the server: a test needs
 * a radio it can script, and `web/src/dev/devTransport.ts` needs one that answers without
 * hardware so demo mode is exercisable at all. It is deliberately a module-level installation
 * rather than a prop or a context value — a screen that took an adapter as a prop would let two
 * screens run two different radios against one gate, and there is exactly one radio.
 *
 * **Nothing driven against a swapped adapter proves anything about a real controller**, on the
 * same terms AGENTS.md §4.7 sets for `devTransport.ts`: §13's BLE clause needs Android hardware
 * and a powered gate, and no suite in this repository can tick it.
 */
export function configureBleAdapter(next: BleAdapter): void {
  installed = next
}

/** The adapter this build runs against. */
export function bleAdapter(): BleAdapter {
  return installed
}

/** Test-only: restore the real Web Bluetooth adapter. */
export function resetBleAdapterForTests(): void {
  installed = webBluetoothAdapter
}
